/**
 * Account → Your sign-ins (Rebuild 50 Chairs #ch21; WORKLIST P4): your own rows from the access log
 * — when · kind · device — who viewed as you, and your devices with Sign out. One button, "That
 * wasn't me — sign out everything", signs out every device and tells the directors.
 *
 * Built here, mounted twice: on the Account page, and in the chair app's More (CC · VOLUNTEERS,
 * F10) — one component, no second copy.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Button, Card, CardRow, Chip, formatDateTime, type ChipKind } from '@cider/ui';
import { api, sentence } from '../../shell/api.js';
import type { Me } from '../../shell/session.js';

interface MyRow {
  id: string;
  at: string;
  kind: 'signin' | 'failed' | 'passkey_added' | 'new_device' | 'token_used';
  lane: string | null;
  device: string | null;
}

interface MyRead {
  id: string;
  at: string;
  kind: string;
  detail: string | null;
}

/** The sensitive kinds in their own words (the access log's third tab). */
const READ_WORDS: Record<string, string> = {
  background_check: 'a background-check result',
  minor: 'a minor’s record',
  drivers_license: 'a driver’s license',
  emergency_contact: 'an emergency contact',
  waiver: 'a signed waiver',
  bank_account: 'the bank account',
  tax_form: 'a W-9 or 1099',
  donor_history: 'a donor’s history',
  people_export: 'the people export',
  signin_export: 'the sign-in log’s export',
};

interface MyDevice {
  id: string;
  device: string | null;
  lane: string;
  since: string;
  current: boolean;
}

const KIND: Record<MyRow['kind'], [string, ChipKind]> = {
  signin: ['signed in', 'ok'],
  failed: ['failed', 'bad'],
  passkey_added: ['passkey added', 'info'],
  new_device: ['new device', 'warn'],
  token_used: ['link used', 'info'],
};

export function YourSignIns({ me }: { me: Me }): ReactNode {
  const [data, setData] = useState<{ rows: MyRow[]; devices: MyDevice[]; reads: MyRead[] } | null>(
    null,
  );
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [round, setRound] = useState(0);
  useEffect(() => {
    let live = true;
    void api<{ rows: MyRow[]; devices: MyDevice[]; reads: MyRead[] }>('GET', '/auth/my-sign-ins')
      .then((d) => {
        if (live) setData(d);
      })
      .catch((err: unknown) => {
        if (live) setRefusal(sentence(err));
      });
    return () => {
      live = false;
    };
  }, [round]);

  const act = (fn: () => Promise<unknown>, after?: () => void): void => {
    setBusy(true);
    setRefusal(null);
    void fn()
      .then(() => {
        if (after) after();
        else setRound((n) => n + 1);
      })
      .catch((err: unknown) => {
        setRefusal(sentence(err));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const viewed = me.viewedAsMe ?? [];
  return (
    <section aria-label="Your sign-ins">
      <h2>Your sign-ins</h2>
      <p>
        Your own rows from the access log, the last thirty days. If one was not you, sign out
        everything — every device, this one too — and the directors are told.
      </p>
      {refusal ? (
        <p className="ui-form__refusal" role="alert">
          {refusal}
        </p>
      ) : null}
      <Card head="THE LAST THIRTY DAYS">
        {(data?.rows ?? []).length === 0 ? (
          <CardRow a={data ? 'No sign-ins in thirty days.' : 'Reading the log…'} />
        ) : (
          (data?.rows ?? []).map((r) => (
            <CardRow
              key={r.id}
              a={`${formatDateTime(r.at)} · ${KIND[r.kind][0]}`}
              c={[r.device ? `“${r.device}”` : null, r.lane].filter(Boolean).join(' · ')}
              chip={<Chip kind={KIND[r.kind][1]}>{KIND[r.kind][0].toUpperCase()}</Chip>}
            />
          ))
        )}
      </Card>
      <Card head="WHAT YOU OPENED">
        {(data?.reads ?? []).length === 0 ? (
          <CardRow a="Nothing sensitive in thirty days." chip={<Chip kind="mute">0</Chip>} />
        ) : (
          (data?.reads ?? []).map((r) => (
            <CardRow
              key={r.id}
              a={`${formatDateTime(r.at)} · ${READ_WORDS[r.kind] ?? r.kind}`}
              c={r.detail ?? undefined}
              chip={<Chip kind="info">LOGGED</Chip>}
            />
          ))
        )}
      </Card>
      <Card head="WHO VIEWED AS ME">
        {viewed.length === 0 ? (
          <CardRow a="Nobody this year." chip={<Chip kind="mute">0</Chip>} />
        ) : (
          viewed.map((v) => (
            <CardRow
              key={`${v.at}-${v.actorId ?? ''}`}
              a={formatDateTime(v.at)}
              c="viewed your screens as you · logged"
              chip={<Chip kind="warn">VIEW AS</Chip>}
            />
          ))
        )}
      </Card>
      <Card head="YOUR DEVICES">
        {(data?.devices ?? []).map((d) => (
          <CardRow
            key={d.id}
            a={d.device ?? 'A device'}
            c={`${d.current ? 'this device · ' : ''}since ${formatDateTime(d.since)}`}
            chip={d.current ? <Chip kind="ok">THIS DEVICE</Chip> : undefined}
            actions={
              d.current ? undefined : (
                <Button
                  kind="quiet"
                  busy={busy}
                  onClick={() => {
                    act(() => api('POST', `/auth/my-sessions/${d.id}/sign-out`));
                  }}
                >
                  Sign out
                </Button>
              )
            }
          />
        ))}
      </Card>
      <p className="ui-row">
        <Button
          kind="primary"
          busy={busy}
          onClick={() => {
            act(
              () => api('POST', '/auth/that-wasnt-me'),
              () => {
                window.location.assign('/login');
              },
            );
          }}
        >
          That wasn’t me — sign out everything
        </Button>
      </p>
    </section>
  );
}
